import { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HorizontalScroller } from '@/components/ui/HorizontalScroller';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { CollectionWithTotal } from '@/api/types';

interface CollectionTabsProps {
    collections: CollectionWithTotal[];
    selectedIds: number[];
    onSelectionChange: (ids: number[]) => void;
    onCollectionReorder?: (collectionId: number, newWeight: number) => void;
}

interface SortableCollectionTabProps {
    collection: CollectionWithTotal;
    isSelected: boolean;
    onToggle: () => void;
}

/**
 * Sortable wrapper for a collection tab button (desktop only).
 */
function SortableCollectionTab({ collection, isSelected, onToggle }: SortableCollectionTabProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: collection.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <button
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            type="button"
            onClick={onToggle}
            className={`
                flex flex-col items-start px-4 py-2 rounded-lg border transition-colors min-w-[140px] shrink-0 cursor-grab active:cursor-grabbing
                ${isSelected
                    ? 'bg-accent/10 border-accent dark:bg-accent/20'
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                }
                ${isDragging ? 'shadow-lg' : ''}
            `}
        >
            <span className={`text-sm font-semibold truncate max-w-full ${
                isSelected
                    ? 'text-accent'
                    : 'text-gray-900 dark:text-white'
            }`}>
                {collection.label}
            </span>
            <MoneyDisplay
                value={collection.total}
                size="sm"
                className={isSelected
                    ? 'text-accent/80'
                    : 'text-gray-500 dark:text-gray-400'}
            />
        </button>
    );
}

/**
 * Static collection tab button (for mobile or non-sortable contexts).
 */
function StaticCollectionTab({ collection, isSelected, onToggle }: SortableCollectionTabProps) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`
                flex flex-col items-start px-4 py-2 rounded-lg border transition-colors min-w-[140px] shrink-0
                ${isSelected
                    ? 'bg-accent/10 border-accent dark:bg-accent/20'
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                }
            `}
        >
            <span className={`text-sm font-semibold truncate max-w-full ${
                isSelected
                    ? 'text-accent'
                    : 'text-gray-900 dark:text-white'
            }`}>
                {collection.label}
            </span>
            <MoneyDisplay
                value={collection.total}
                size="sm"
                className={isSelected
                    ? 'text-accent/80'
                    : 'text-gray-500 dark:text-gray-400'}
            />
        </button>
    );
}

/**
 * Horizontal tab-style collection filter.
 * - Each tab shows label + total value
 * - Toggle additive selection: click toggles on/off
 * - When no collections selected = all selected (show all ledgers)
 * - Horizontal scrolling with left/right arrow affordances when tabs overflow
 * - Desktop: Tabs are draggable for reordering
 * - Mobile: Tabs are static (no drag)
 */
export function CollectionTabs({
    collections,
    selectedIds,
    onSelectionChange,
    onCollectionReorder,
}: CollectionTabsProps) {
    // dnd-kit sensors for desktop drag
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require minimum drag distance before activating
            },
        })
    );

    // Local state for optimistic reordering - prevents flicker during API call
    const [localOrder, setLocalOrder] = useState<CollectionWithTotal[]>(collections);

    // Sync local order when collections change (new data from server)
    useEffect(() => {
        setLocalOrder(collections);
    }, [collections]);

    const handleToggle = (collectionId: number) => {
        if (selectedIds.includes(collectionId)) {
            // Deselect
            onSelectionChange(selectedIds.filter((id) => id !== collectionId));
        } else {
            // Select
            onSelectionChange([...selectedIds, collectionId]);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id || !onCollectionReorder) {
            return;
        }

        const oldIndex = localOrder.findIndex((c) => c.id === active.id);
        const newIndex = localOrder.findIndex((c) => c.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        // Immediately update local order for smooth visual feedback
        setLocalOrder(arrayMove(localOrder, oldIndex, newIndex));

        // Notify parent to persist the change
        onCollectionReorder(Number(active.id), newIndex);
    };

    // If none are selected, treat as all selected
    const allSelected = selectedIds.length === 0;

    // When no selection (all implicitly selected), show all as unselected visually
    const isSelected = (id: number) => !allSelected && selectedIds.includes(id);

    return (
        <div>
            {/* Desktop: Sortable tabs with horizontal scrolling */}
            <HorizontalScroller label="Collections" className="hidden lg:block">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={localOrder.map((c) => c.id)}
                        strategy={horizontalListSortingStrategy}
                    >
                        {localOrder.map((collection) => (
                            <SortableCollectionTab
                                key={collection.id}
                                collection={collection}
                                isSelected={isSelected(collection.id)}
                                onToggle={() => handleToggle(collection.id)}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </HorizontalScroller>

            {/* Mobile: Horizontally scrollable static tabs */}
            <HorizontalScroller label="Collections" className="lg:hidden">
                {localOrder.map((collection) => (
                    <StaticCollectionTab
                        key={collection.id}
                        collection={collection}
                        isSelected={isSelected(collection.id)}
                        onToggle={() => handleToggle(collection.id)}
                    />
                ))}
            </HorizontalScroller>
        </div>
    );
}
